import axios from "axios";
import { API_BASE_URL } from "../../config";
import { authHeaders } from "../utils/session";

/**
 * Feature launches (2026-10-26) — the "new feature" announcements the user dashboard shows
 * as a header pill and a "New on Entermark" card. The dashboard turns each into a call to
 * action from the user's plan (`feature_key`) and AI wallet (`credit_action`).
 *
 * Reading is open to any CMS user; every write is superadmin-only and answers 403 otherwise.
 */

const jsonHeaders = () => ({
  ...authHeaders(),
  "Content-Type": "application/json",
});

/** Turn an API failure into the message the user should actually read. */
const apiError = (err, fallback) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (status === 403) return new Error(data?.message || "Only a superadmin can manage feature launches.");
  if (status === 404) return new Error(data?.message || "Feature launch not found.");
  return new Error(data?.message || data?.error || fallback);
};

const unwrap = (res, fallback) => {
  const body = res.data || {};
  if (body.status && body.status !== 200 && body.status !== true) {
    throw new Error(body.message || fallback);
  }
  return body.data ?? body;
};

export const fetchFeatureLaunches = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/feature-launches`, { headers: jsonHeaders() });
    const data = unwrap(res, "Failed to load feature launches");
    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw apiError(err, "Failed to load feature launches");
  }
};

/** The form's two dropdowns: plan features and credit actions. */
export const fetchFeatureLaunchMeta = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/feature-launches/meta`, { headers: jsonHeaders() });
    const data = unwrap(res, "Failed to load the form options");
    return { features: data?.features || [], creditActions: data?.creditActions || [] };
  } catch (err) {
    throw apiError(err, "Failed to load the form options");
  }
};

export const createFeatureLaunch = async (payload) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/feature-launches`, payload, { headers: jsonHeaders() });
    return unwrap(res, "Failed to create this launch");
  } catch (err) {
    throw apiError(err, "Failed to create this launch");
  }
};

export const updateFeatureLaunch = async (id, payload) => {
  try {
    const res = await axios.put(`${API_BASE_URL}/feature-launches/${id}`, payload, { headers: jsonHeaders() });
    return unwrap(res, "Failed to update this launch");
  } catch (err) {
    throw apiError(err, "Failed to update this launch");
  }
};

export const toggleFeatureLaunchStatus = async (id) => {
  try {
    const res = await axios.patch(`${API_BASE_URL}/feature-launches/${id}/toggle-status`, {}, { headers: jsonHeaders() });
    return unwrap(res, "Failed to change the status");
  } catch (err) {
    throw apiError(err, "Failed to change the status");
  }
};

export const deleteFeatureLaunch = async (id) => {
  try {
    const res = await axios.delete(`${API_BASE_URL}/feature-launches/${id}`, { headers: jsonHeaders() });
    return res.data?.message || "Feature launch deleted";
  } catch (err) {
    throw apiError(err, "Failed to delete this launch");
  }
};
